import React, { useEffect, useState, useCallback } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro, Card } from "@/admin/ui";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Palette, Globe, Layers, Phone, ToggleLeft, ShieldCheck, Lock, AlertTriangle, Trash2 } from "lucide-react";

const STATUSES = ["upcoming", "live", "completed", "archived"];
const CONTACTS = [
  ["events_help", "Events Help"],
  ["travel_help", "Travel Help"],
  ["onsite", "On-site Support"],
  ["emergency", "Emergency"],
];
const FEATURES = [
  ["schedule", "Schedule / My Agenda"],
  ["explore", "Explore"],
  ["trip", "My Trip"],
  ["photos", "Photos"],
  ["challenge", "Challenge & Rewards"],
];
const TIMEZONES = ["America/Mazatlan", "America/Mexico_City", "America/Los_Angeles",
  "America/Denver", "America/Chicago", "America/New_York", "UTC"];

export default function Settings() {
  const { eventId, refreshEvents } = useAdminEvent();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [tzWarn, setTzWarn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newMgr, setNewMgr] = useState("");

  const load = useCallback(() => {
    api.mgrSettings(eventId).then((d) => { setData(d); setF({ ...d.event, contacts: d.contacts, feature_visibility: d.feature_visibility }); }).catch(() => {});
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  if (!f) return <div className="h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setContact = (k, field, v) => setF((p) => ({ ...p, contacts: { ...p.contacts, [k]: { ...p.contacts[k], [field]: v } } }));
  const setFeature = (k, v) => setF((p) => ({ ...p, feature_visibility: { ...p.feature_visibility, [k]: v } }));

  const doSave = async () => {
    setSaving(true);
    try {
      const body = {
        name: f.name, destination: f.destination, host_resort: f.host_resort,
        date_label: f.date_label, start_date: f.start_date, end_date: f.end_date,
        status: f.status, timezone: f.timezone, contacts: f.contacts,
        feature_visibility: f.feature_visibility,
      };
      const d = await api.mgrSettingsUpdate(eventId, body);
      setData(d); setF({ ...d.event, contacts: d.contacts, feature_visibility: d.feature_visibility });
      if (refreshEvents) refreshEvents();
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); setTzWarn(null); }
  };

  const onSave = () => {
    if (f.timezone !== data.event.timezone) { setTzWarn(f.timezone); return; }
    doSave();
  };

  const act = async (fn, msg) => {
    try { await fn(); const d = await api.mgrSettings(eventId); setData(d); toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div>
      <PageIntro>Manage this event's core details, on-site contacts and which features appear in the attendee app. The Event ID and brand palette are system-managed.</PageIntro>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-[hsl(var(--secondary))]" /><h3 className="font-serif text-xl text-[hsl(var(--primary))]">Event Details</h3></div>
          <div className="space-y-3">
            <div><Label>Display name</Label><Input data-testid="set-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Destination</Label><Input data-testid="set-destination" value={f.destination || ""} onChange={(e) => set("destination", e.target.value)} /></div>
              <div><Label>Venue / resort</Label><Input value={f.host_resort || ""} onChange={(e) => set("host_resort", e.target.value)} /></div>
            </div>
            <div><Label>Dates label</Label><Input value={f.date_label || ""} onChange={(e) => set("date_label", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Start date</Label><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></div>
              <div><Label>End date</Label><Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Status</Label>
                <Select value={f.status || "upcoming"} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="set-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1">Timezone <AlertTriangle className="h-3 w-3 text-[hsl(var(--pc-gold))]" /></Label>
                <Select value={f.timezone || "UTC"} onValueChange={(v) => set("timezone", v)}>
                  <SelectTrigger data-testid="set-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[hsl(var(--muted)/0.5)] px-3 py-2">
              <span className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Event ID (system-managed)</span>
              <code className="text-xs font-mono text-[hsl(var(--primary))]">{data.event.id}</code>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-3"><Phone className="h-4 w-4 text-[hsl(var(--secondary))]" /><h3 className="font-serif text-xl text-[hsl(var(--primary))]">Support Contacts</h3></div>
          <div className="space-y-3">
            {CONTACTS.map(([k, label]) => (
              <div key={k} className="rounded-lg border border-[hsl(var(--border))] p-3">
                <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-1.5">{label}</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid={`contact-${k}-phone`} placeholder="Phone" value={f.contacts[k]?.phone || ""} onChange={(e) => setContact(k, "phone", e.target.value)} />
                  <Input data-testid={`contact-${k}-email`} placeholder="Email" value={f.contacts[k]?.email || ""} onChange={(e) => setContact(k, "email", e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-3"><ToggleLeft className="h-4 w-4 text-[hsl(var(--secondary))]" /><h3 className="font-serif text-xl text-[hsl(var(--primary))]">Attendee App Features</h3></div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Toggle which sections appear in the attendee app bottom navigation.</p>
          <div className="space-y-2">
            {FEATURES.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-3 py-2.5">
                <span className="text-sm text-[hsl(var(--primary))]">{label}</span>
                <Switch data-testid={`feature-${k}`} checked={!!f.feature_visibility[k]} onCheckedChange={(v) => setFeature(k, v)} />
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          <Card>
            <div className="flex items-center gap-2 mb-3"><ShieldCheck className="h-4 w-4 text-[hsl(var(--secondary))]" /><h3 className="font-serif text-xl text-[hsl(var(--primary))]">Manager Access</h3></div>
            <div className="space-y-2">
              {(data.managers || []).map((m) => (
                <div key={m.email} className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm gap-2">
                  <span className="text-[hsl(var(--primary))] truncate flex items-center gap-1.5">{m.email}{m.is_test && <span className="text-[9px] uppercase font-bold rounded bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] px-1.5 py-0.5">Test</span>}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${m.role === "Platform Admin" ? "bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))]" : "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]"}`}>{m.role}</span>
                    {data.can_manage_access && m.role !== "Platform Admin" && (
                      <button data-testid={`revoke-${m.email}`} onClick={() => act(() => api.mgrManagerRevoke(eventId, m.email), "Access revoked")} className="text-[hsl(var(--pc-gold-2))] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                </div>
              ))}
              {(!data.managers || data.managers.length === 0) && <div className="text-sm text-[hsl(var(--muted-foreground))]">No managers assigned.</div>}
            </div>
            {data.can_manage_access ? (
              <div className="mt-3 flex gap-2" data-testid="add-manager-row">
                <Input placeholder="name@collabera.com" value={newMgr} onChange={(e) => setNewMgr(e.target.value)} data-testid="new-manager-email" />
                <Button data-testid="add-manager-btn" onClick={() => act(async () => { await api.mgrManagerGrant(eventId, { email: newMgr }); setNewMgr(""); }, "Event Manager access granted")}>Add</Button>
              </div>
            ) : (
              <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Only a Platform Admin can add or revoke manager access.</p>
            )}
          </Card>
          <Card>
            <div className="flex items-center gap-2 mb-3"><Palette className="h-4 w-4 text-[hsl(var(--secondary))]" /><h3 className="font-serif text-xl text-[hsl(var(--primary))]">Brand Palette <span className="text-[10px] uppercase text-[hsl(var(--muted-foreground))] ml-1">Protected</span></h3></div>
            <div className="grid grid-cols-4 gap-2">
              {(data.brand_palette || []).map((c) => (
                <div key={c.hex} className="text-center"><div className="h-12 w-full rounded-lg border border-[hsl(var(--border))]" style={{ background: c.hex }} /><div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{c.name}</div></div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button data-testid="settings-save" onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </div>

      <Dialog open={!!tzWarn} onOpenChange={(o) => !o && setTzWarn(null)}>
        <DialogContent data-testid="tz-warn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[hsl(var(--pc-gold))]" /> Change event timezone?</DialogTitle>
            <DialogDescription>
              Changing the timezone from <b>{data.event.timezone}</b> to <b>{tzWarn}</b> affects how every session time, announcement schedule and journey countdown is interpreted across the attendee app. Only change this if the event location's timezone is genuinely different.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { set("timezone", data.event.timezone); setTzWarn(null); }}>Keep {data.event.timezone}</Button>
            <Button data-testid="tz-confirm" onClick={doSave}>Change timezone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
